import React, { useMemo } from "react";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalBadge from "../../../../../global/GlobalBadge";
import GlobalDirectoryFiltersSection from "../../../../../global/directory/GlobalDirectoryFiltersSection";
import GlobalDirectoryGridSection from "../../../../../global/directory/GlobalDirectoryGridSection";
import {
  GlobalDirectoryShell,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../../../global/directory";

/** --- Column config (directory table, export, column picker) --- */

export const MEMBERSHIP_LIST_COLUMN_VISIBILITY_KEY =
  "girinestham.membershipList.columnVisibility";

export const MEMBERSHIP_DIRECTORY_COLUMN_DEFS = [
  { id: "membership_name", label: "Name", defaultVisible: true },
  { id: "type_name", label: "Type", defaultVisible: true },
  { id: "duration_name", label: "Duration", defaultVisible: true },
  { id: "fee_type_name", label: "Fee category", defaultVisible: false },
  { id: "price", label: "Subscription price", defaultVisible: true },
  { id: "registration_fee", label: "Joining fee", defaultVisible: false },
  { id: "discount", label: "Discount (%)", defaultVisible: false },
  {
    id: "total_students_registered",
    label: "Students enrolled",
    defaultVisible: true,
  },
  { id: "total_amount_paid", label: "Total collected", defaultVisible: false },
  { id: "max_students_allowed", label: "Enrollment cap", defaultVisible: false },
  { id: "status_name", label: "Publishing status", defaultVisible: true },
  { id: "description", label: "Description", defaultVisible: false },
  { id: "benefits", label: "Benefits", defaultVisible: false },
];

const DIRECTORY_FILTER_COLUMNS = MEMBERSHIP_DIRECTORY_COLUMN_DEFS;

function loadMembershipColumnVisibility() {
  try {
    const raw = localStorage.getItem(MEMBERSHIP_LIST_COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialMembershipColumnVisibility() {
  const saved = loadMembershipColumnVisibility();
  const initial = {};
  for (const c of MEMBERSHIP_DIRECTORY_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

function formatMembershipMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "—";
}

export function membershipDirectoryCellValue(columnId, m) {
  switch (columnId) {
    case "price":
    case "registration_fee":
    case "total_amount_paid":
      return formatMembershipMoney(m[columnId]);
    case "discount":
      if (m.discount == null || m.discount === "") return "—";
      return `${Number(m.discount)}%`;
    case "description":
    case "benefits": {
      const t = m[columnId];
      if (t == null || String(t).trim() === "") return "—";
      const s = String(t);
      return s.length > 120 ? `${s.slice(0, 117)}…` : s;
    }
    default:
      return m[columnId] ?? "—";
  }
}

/** --- Filters --- */

/** Initial empty filters for the membership plans directory (avoids name clash with stale MembershipFilters imports). */
export function membershipDefaultDirectoryFilters() {
  return {
    plan_search: "",
    column: "",
    value: "",
  };
}

function MembershipFilters({
  draftFilters,
  onDraftChange,
  onClear,
  memberships,
  secondaryStart = null,
  secondaryEnd = null,
}) {
  /** Value options come only from rows currently in the directory grid. */
  const valueFilterOptions = useMemo(() => {
    const col = draftFilters.column;
    if (!col) return [];
    const seen = new Set();
    const opts = [];
    for (const row of memberships) {
      const label = String(membershipDirectoryCellValue(col, row) ?? "").trim();
      if (!label || label === "—") continue;
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ value: label, label });
      }
    }
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [draftFilters.column, memberships]);

  return (
    <GlobalDirectoryFiltersSection
      className="ub-filters ub-ms-directory-filters"
      searchLabel="Search by membership plan"
      searchPlaceholder="Type plan name"
      searchValue={draftFilters.plan_search}
      onSearchChange={(v) => onDraftChange({ ...draftFilters, plan_search: v })}
      columnLabel="Column"
      columnValue={draftFilters.column}
      onColumnChange={(v) =>
        onDraftChange({ ...draftFilters, column: v, value: "" })
      }
      gridColumnDefs={DIRECTORY_FILTER_COLUMNS}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.value}
      onValueChange={(v) => onDraftChange({ ...draftFilters, value: v })}
      valueOptions={valueFilterOptions}
      valuePlaceholderOption="All"
      onClear={onClear}
      clearFilterLabel="Clear filter"
      secondaryStart={secondaryStart}
      secondaryEnd={secondaryEnd}
    />
  );
}

/** --- List table --- */

function MembershipListTable({
  memberships,
  columnVisibility,
  onActivate,
  onDeactivate,
  onDelete,
  onRequestEdit,
  loading,
  canEdit = false,
  canDelete = false,
}) {
  const visibleDataColumns = useMemo(
    () => MEMBERSHIP_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions",
      },
    ],
    [visibleDataColumns],
  );

  const showRows = !loading && memberships.length > 0;

  return (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading memberships…"
      headers={tableHeaders}
      isEmpty={!loading && memberships.length === 0}
      emptyMessage="No memberships found."
      tableAriaLabel="Memberships list table"
      wrapClassName="ub-table-wrap"
      tableClassName="ub-table"
    >
      {showRows
        ? memberships.map((m) => (
            <tr key={m.id}>
              {visibleDataColumns.map((col) => (
                <td key={col.id}>
                  {col.id === "status_name" ? (
                    <GlobalBadge
                      active={(m.status_name || "").toLowerCase() === "active"}
                      activeText={m.status_name}
                      inactiveText={m.status_name}
                    />
                  ) : col.id === "membership_name" ? (
                    <span style={{ fontWeight: 500 }}>
                      {membershipDirectoryCellValue(col.id, m)}
                    </span>
                  ) : (
                    membershipDirectoryCellValue(col.id, m)
                  )}
                </td>
              ))}
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  {canEdit ? (
                    <GlobalTableActionButton
                      action="edit"
                      title="Edit membership in form"
                      onClick={() => onRequestEdit?.(m)}
                    />
                  ) : null}

                  {canEdit ? (
                    (m.status_name || "").toLowerCase() === "active" ? (
                      <GlobalTableActionButton
                        action="deactivate"
                        variant="ghost"
                        onClick={() => onDeactivate(m.id)}
                      />
                    ) : (
                      <GlobalTableActionButton
                        action="activate"
                        onClick={() => onActivate(m.id)}
                      />
                    )
                  ) : null}

                  {canDelete && m.total_students_registered === 0 ? (
                    <GlobalTableActionButton
                      action="delete"
                      onClick={() => onDelete(m.id)}
                    />
                  ) : null}
                </div>
              </td>
            </tr>
          ))
        : null}
    </GlobalDirectoryGridSection>
  );
}

/** --- Section shell --- */

export default function MembershipDirectorySection({
  memberships,
  draftDirectoryFilters,
  onDraftDirectoryFiltersChange,
  onClearDirectoryFilters,
  columnVisibility,
  onColumnVisibilityChange,
  getMembershipExportData,
  loadingMemberships,
  directoryPage,
  directoryTotalPages,
  directoryTotal,
  directoryPageSize,
  onDirectoryPageChange,
  onActivate,
  onDeactivate,
  onDelete,
  onRequestEditMembership,
  canEdit = false,
  canDelete = false,
}) {
  return (
    <div
      id="ms-list"
      className="user-builder-panel"
      style={{ scrollMarginTop: "2rem" }}
    >
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Directory</h3>
        <p className="user-builder-panel__hint">
          Search and manage all membership plans.
        </p>
      </div>
      <GlobalDirectoryShell
        filters={
          <MembershipFilters
            draftFilters={draftDirectoryFilters}
            onDraftChange={onDraftDirectoryFiltersChange}
            onClear={onClearDirectoryFilters}
            memberships={memberships}
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={MEMBERSHIP_DIRECTORY_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={onColumnVisibilityChange}
                storageKey={MEMBERSHIP_LIST_COLUMN_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getMembershipExportData}
                filenameBase="memberships_directory"
              />
            }
          />
        }
        approvals={null}
        grid={
          <MembershipListTable
            memberships={memberships}
            columnVisibility={columnVisibility}
            loading={loadingMemberships}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
            onDelete={onDelete}
            onRequestEdit={onRequestEditMembership}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={directoryPage}
            totalPages={directoryTotalPages}
            total={directoryTotal}
            pageSize={directoryPageSize}
            onPageChange={onDirectoryPageChange}
            label="Membership directory pagination"
          />
        }
      />
    </div>
  );
}
